'use client';

import { ReactNode, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Content } from '@/components/Content';
import { usePathname } from 'next/navigation';

// Erstellen einer erweiterten Content-Komponente mit Framer Motion
const MotionContent = motion.create(Content);

import { forceMainThreadAnimation } from '@/utils/motion';

// WICHTIG: Dieses (leere) onUpdate-Prop erzwingt die Main-Thread-Animation
// statt WAAPI (AcceleratedAnimation.supports() in framer-motion prüft auf
// onUpdate). Hintergrund: framer-motion lässt bei WAAPI style.opacity auf 0
// stehen und schreibt den Endwert erst im onfinish-Task. Zwischen dem Ende
// der Compositor-Animation und diesem Commit rendert Chrome einen Frame, in
// dem das Element auf style.opacity 0 zurückfällt → der komplette Seiten-
// inhalt verschwindet für einen Frame (sichtbares „Flackern" am Ende der
// Einblendung, per CDP-Screencast nachgewiesen). Die Main-Thread-Animation
// schreibt opacity jeden Frame inline, da kann diese Lücke nicht entstehen.

export default function Template({ children }: { children: ReactNode }) {
  const pathname = usePathname();

  // Beim Erst-Load KEINE Einblend-Animation: sonst rendert SSR opacity:0
  // und der Content erscheint erst nach der Hydration (sichtbares Flackern
  // auf hellen Seiten). Erst ab der ersten Client-Navigation wird animiert.
  const hasMounted = useRef(false);
  useEffect(() => { hasMounted.current = true; }, []);

  
  return (
    <AnimatePresence
      mode="wait"
      onExitComplete={() => {
        // Scroll-Reset ERST nach der Exit-Animation: <main> ist der gemeinsame
        // Scroller — ein Reset während des Ausblendens lässt den alten Content
        // an seinen Anfang springen (sichtbares Aufblitzen, z. B. beim Weg vom
        // gescrollten Blog-Artikel zurück zur Liste). Next setzt bei Navigation
        // nur window zurück, daher bleibt der manuelle Reset nötig (Footer-
        // Klicks von ganz unten müssen oben landen).
        document.querySelector('main')?.scrollTo(0, 0);
      }}
    >
      <MotionContent>
        <motion.div
          key={pathname}
          // Flex-Item in .content--wrapper: min-w-0 hebt die min-content-Sperre auf,
          // max-w-full verhindert, dass breiter Inhalt (z.B. <pre>) den Viewport sprengt
          className="min-w-0 max-w-full"
          initial={hasMounted.current ? { opacity: 0 } : false}
          animate={{ opacity: 1, transition: { duration: 0.6, ease: "easeOut" } }}
          exit={{ opacity: 0, transition: { duration: 0.25, ease: "easeIn" } }}
          onUpdate={forceMainThreadAnimation}
        >
          {children}
        </motion.div>
      </MotionContent>
    </AnimatePresence>
  );
}